import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import LabelBox from "~/SalesCampaign/components/LabelBox/LabelBox";
import classes from "./NewContractTable.module.less";
import { Link } from "~/common/components/Link";
import { DateFormatType, formatDate } from "~/utils/DateFormatter";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { useState } from "react";
import classNames from "classnames";
import type { UpdatedContractOverviewData } from "~/Contracts/redux/types";
import { useTranslation } from "react-i18next";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface NewContractTableProps {
  contractData: UpdatedContractOverviewData[];
}

export enum ContractSort {
  contractNumber = "contractNumber",
  contractPeriod = "contractPeriod",
  designation = "designation",
  schaefflerId = "schaefflerId",
  customerMaterialNumber = "customerMaterialNumber",
  totalPrice = "totalPrice",
  disposition = "disposition",
  status = "status",
}

export enum Status {
  // This ordering decides the "importance" of each status for the sorting
  Active,
  Waiting,
  Completed,
  Cancelled,
}

const STATUS_CODES_MAP = {
  "codes.contractStatus.01": Status.Waiting,
  "codes.contractStatus.02": Status.Active,
  "codes.contractStatus.05": Status.Completed,
  "codes.contractStatus.06": Status.Completed,
  "codes.contractStatus.07": Status.Cancelled,
};

const ICON_MAP = {
  [Status.Active]: IconType.Active,
  [Status.Waiting]: IconType.Clock,
  [Status.Completed]: IconType.Check,
  [Status.Cancelled]: IconType.Close,
};

const sortDates = (firstDate?: Date, secondDate?: Date) => {
  const aDate = firstDate ? firstDate : null;
  const bDate = secondDate ? secondDate : null;

  if (aDate === null && bDate === null) {
    return 0;
  }
  if (aDate === null) {
    return 1;
  }
  if (bDate === null) {
    return -1;
  }
  return aDate.valueOf() - bDate.valueOf();
};

const SORT_HANDLERS: Dict<(a: UpdatedContractOverviewData, b: UpdatedContractOverviewData) => number> = {
  [ContractSort.contractNumber]: (a, b) => a.contractNumber.localeCompare(b.contractNumber),
  [ContractSort.contractPeriod]: (a, b) => {
    if (!a.startDate || !a.endDate) {
      return !b.startDate || !b.endDate ? 0 : 1;
    }
    if (!b.startDate || !b.endDate) {
      return -1;
    }
    return a.startDate === b.startDate ? 0 : a.startDate > b.startDate ? 1 : -1;
  },
  [ContractSort.designation]: (a, b) => a.designation.localeCompare(b.designation),
  [ContractSort.schaefflerId]: (a, b) => parseInt(a.materialNumber) - parseInt(b.materialNumber),
  [ContractSort.customerMaterialNumber]: (a, b) => parseInt(a.contractNumber) - parseInt(b.contractNumber),
  [ContractSort.totalPrice]: (a, b) => a.netPrice.value - b.netPrice.value,
  [ContractSort.disposition]: (a, b) => sortDates(a.disposition, b.disposition),
  [ContractSort.status]: (a, b) => {
    const statusComparison = STATUS_CODES_MAP[a.status] - STATUS_CODES_MAP[b.status];

    if (statusComparison === 0) {
      return sortDates(a.disposition, b.disposition);
    } else {
      return statusComparison;
    }
  },
};

const NewContractTable = ({ contractData }: NewContractTableProps) => {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(ContractSort.status);
  const [sortDirection, setSortDirection] = useState(SortDirection.Asc);

  const rows = useSorting(contractData, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: ContractSort) => () => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: ContractSort) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const contractEntries = rows?.map((contract, index) => {
    const contractPeriod =
      contract.startDate &&
      contract.endDate &&
      `${formatDate(contract.startDate, DateFormatType.DATE)} - ${formatDate(contract.endDate, DateFormatType.DATE)}`;
    const disposition = contract.disposition && formatDate(contract.disposition, DateFormatType.DATE);
    return (
      <TableRow key={index}>
        <TableCell className={classNames(classes.cell, classes.contractNumber)}>
          <p>{contract.contractNumber ?? "-"}</p>
        </TableCell>
        <TableCell className={classNames(classes.cell, classes.period)}>
          <p>{contractPeriod ?? "-"}</p>
        </TableCell>
        <TableCell className={classes.cell}>
          <p className={"f-no-wrap"}>{contract.designation ?? "-"}</p>
        </TableCell>
        <TableCell className={classes.cell}>
          <p>{contract.materialNumber ?? "-"}</p>
        </TableCell>
        <TableCell className={classes.cell}>
          <p>{contract.customerMaterialNumber ?? "-"}</p>
        </TableCell>
        <TableCell className={classNames(classes.cell, classes.netPrice)}>
          <p>{contract.netPrice.formattedValue}</p>
        </TableCell>
        <TableCell className={classes.cell}>
          <p>{disposition ?? "-"}</p>
        </TableCell>
        <TableCell className={classNames(classes.cell, classes.status)}>
          <LabelBox variant="darkGrey" className={classes.labelBox}>
            <Stacker gap={4} direction={"row"} className={classes.chip}>
              <Icon className={"d-flex"} type={ICON_MAP[STATUS_CODES_MAP[contract.status]]} />
              {t(contract.status) ?? "-"}
            </Stacker>
          </LabelBox>
        </TableCell>
        <TableCell className={classNames(classes.align, classes.contractDetails)}>
          <Link href={contract.detailsUrl}>{t("contractOverview.table.details")}</Link>
        </TableCell>
      </TableRow>
    );
  });
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.contractNumber)}>
              {t("contractOverview.table.contractNo")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.contractPeriod)}>
              {t("contractOverview.table.period")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.designation)}>
              {t("contractOverview.table.designation")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.schaefflerId)}>
              {t("productComparison.download.schaefflerId")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.customerMaterialNumber)}>
              {t("contractOverview.searchType.customerMaterialNumber")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              disabled
              hideSortIcon
              className={classes.headerLabel}
              {...createTableProps(ContractSort.totalPrice)}
            >
              {t("contractOverview.table.netPrice")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.disposition)}>
              {t("contractOverview.table.disposition")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerLabel} {...createTableProps(ContractSort.status)}>
              {t("quoteDetailsPage.table.header.status")}
            </TableSortLabel>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>

      <TableBody>{contractEntries}</TableBody>
    </Table>
  );
};

export default NewContractTable;
